import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProgressToolCall, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {ToolType, getToolIcon} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export interface CitationSourceChipProps {
  citationId: string;
  citationNumber: number;
  onCitationClick: (toolId: string, isSourceCitation?: boolean) => void;
  toolCall: ProgressToolCall | undefined;
}

export default function CitationSourceChip({
  citationId,
  citationNumber,
  onCitationClick,
  toolCall,
}: CitationSourceChipProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();

  const handleClick = React.useCallback((): void => {
    onCitationClick(citationId, true);
  }, [onCitationClick, citationId]);

  const toolDescription = toolCall
    ? getToolDisplayNameFormatted(toolCall.tool, toolCall.args, ToolCallStatus.COMPLETED)
    : 'View citation details';

  const tooltipContent = (
    <div>
      <div>Activity: {toolDescription}</div>
      <div>Citation #{citationNumber}</div>
    </div>
  );

  // For ReadBenchlingDocumentTool, extract document_id from args to render EntryChip
  const documentId =
    toolCall?.tool === ToolType.ReadBenchlingDocumentTool
      ? (toolCall.args?.document_id as string | undefined)
      : undefined;

  return (
    <li className={css(styles.container)} value={citationNumber}>
      <Tooltip text={tooltipContent} placement={FloatingPlacement.TOP}>
        {documentId ? (
          <EntryChip id={documentId} size={ChipSize.SMALL} onClick={handleClick} />
        ) : (
          <Chip
            size={ChipSize.SMALL}
            text={toolCall ? toolDescription : ''}
            onClick={handleClick}
            icon={toolCall ? getToolIcon(toolCall.tool) : undefined}
          />
        )}
      </Tooltip>
    </li>
  );
}

export interface WebCitationSourceProps {
  url: string;
}

export function WebCitationSource({url}: WebCitationSourceProps): JSX.Element | null {
  let cleanUrl = '';
  let domain = '';
  let isValidUrl = true;

  try {
    const urlObj = new URL(url);
    cleanUrl = `${urlObj.origin}${urlObj.pathname}`;
    domain = urlObj.hostname;
  } catch (error) {
    if (error instanceof TypeError) {
      isValidUrl = false;
      cleanUrl = url;
      domain = 'Invalid URL';
    } else {
      throw error;
    }
  }

  const handleClick = React.useCallback((): void => {
    window.open(cleanUrl, '_blank', 'noopener,noreferrer');
  }, [cleanUrl]);

  if (!isValidUrl) {
    return null;
  }

  return (
    <li className={css(styles.webCitationItem)}>
      <Tooltip text={cleanUrl} placement={FloatingPlacement.TOP}>
        <Chip size={ChipSize.SMALL} text={domain} onClick={handleClick} icon="globe" />
      </Tooltip>
    </li>
  );
}

const styles = StyleSheet.create({
  container: {
    listStylePosition: 'inside',
  },
  citationNumber: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  webCitationItem: {
    listStyle: 'none',
  },
});
